"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { StageTabs } from "@/components/ui/StageTabs";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { FUSportsStandingsEntryWithNativeTeamId, StageStandings } from "@/utilities/data/types";
export interface StandingsMiniProps {
  /** Either a flat entries list (legacy / single-stage) or a list of stages. */
  standings: FUSportsStandingsEntryWithNativeTeamId[] | StageStandings[];
  /** Whether to show the header bar. Defaults to true. */
  showHeader?: boolean;
  /** Override the standings URL. Defaults to nav.standings(). */
  standingsUrl?: string;
  /** Maximum number of teams to show. Defaults to 20. */
  maxRows?: number;
  /** Optional competition logo URL for the header. */
  competitionLogo?: string;
}
function isStageStandingsArray(s: FUSportsStandingsEntryWithNativeTeamId[] | StageStandings[]): s is StageStandings[] {
  return s.length > 0 && typeof (s[0] as StageStandings).stage !== "undefined";
}

/** Get a 3-letter code. Prefers shortName (≤4 chars), falls back to first 3 of name. */
function getTeamCode(name: string, shortName?: string): string {
  if (shortName && shortName.length <= 4) return shortName.toUpperCase();
  return name.slice(0, 3).toUpperCase();
}

/**
 * StandingsMini — Compact standings table for sidebar placement.
 * Shows POS, CLUB (logo + 3-letter code), PTS, P, GD.
 * Designed to sit next to the hero carousel.
 */
export const StandingsMini: React.FC<StandingsMiniProps> = t0 => {
  const $ = _c(27);
  const {
    standings,
    showHeader: t1,
    standingsUrl,
    maxRows: t2,
    competitionLogo
  } = t0;
  const showHeader = t1 === undefined ? true : t1;
  const maxRows = t2 === undefined ? 20 : t2;
  const labels = useLabels();
  const nav = useNavigation();
  let t3;
  if ($[0] !== nav || $[1] !== standingsUrl) {
    t3 = standingsUrl ?? nav.standings();
    $[0] = nav;
    $[1] = standingsUrl;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const fullTableUrl = t3;
  let t4;
  if ($[3] !== standings) {
    t4 = isStageStandingsArray(standings) ? standings : standings.length > 0 ? [{
      stage: {
        id: "single",
        name: ""
      } as StageStandings["stage"],
      standings: {
        entries: standings
      } as StageStandings["standings"]
    }] : [];
    $[3] = standings;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const stages = t4;
  if (stages.length === 0) {
    return null;
  }
  let t5;
  if ($[5] !== labels || $[6] !== stages) {
    let t6;
    if ($[8] !== labels) {
      t6 = s => ({
        id: s.stage.id,
        label: s.stage.name || labels.standings.title
      });
      $[8] = labels;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = stages.map(t6);
    $[5] = labels;
    $[6] = stages;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const tabs = t5;
  let t6;
  if ($[10] !== labels || $[11] !== maxRows || $[12] !== nav || $[13] !== stages) {
    t6 = stageId => {
      const found = stages.find(s_0 => s_0.stage.id === stageId);
      if (!found) {
        return null;
      }
      const allEntries = found.standings.entries;
      const entries = allEntries.length > maxRows ? allEntries.slice(0, maxRows) : allEntries;
      if (entries.length === 0) {
        return null;
      }
      return <><div className="flex items-center px-4 py-2 border-b text-[11px] font-bold uppercase tracking-wider opacity-60" style={{
          borderColor: "var(--table-divider)"
        }}><span className="w-7">#</span><span className="flex-1 ml-1">{labels.standings.table.team}</span><span className="w-9 text-right">{labels.standings.table.points}</span><span className="w-8 text-right">{labels.standings.table.played}</span><span className="w-8 text-right">{labels.standings.table.goalDifference}</span></div><div className="flex-1 overflow-y-auto">{entries.map((entry, index) => {
            const rank = entry.rank ?? index + 1;
            const team = entry.competitor;
            const points = entry.stats?.points ?? 0;
            const played = entry.stats?.played ?? 0;
            const gd = entry.stats?.goalDifference ?? 0;
            const teamUrl = team.fansUnitedTeamId ? nav.team(team.fansUnitedTeamId) : "#";
            const code = getTeamCode(team.name, team.shortName);
            return <Link prefetch={false} key={team.id} href={teamUrl} className="flex items-center px-4 py-2.5 hover:opacity-90 transition-colors cursor-pointer" style={index < entries.length - 1 ? {
              borderBottom: "1px solid var(--table-divider)"
            } : undefined}><span className="w-7 text-sm opacity-60 tabular-nums font-semibold">{rank}</span><TeamLogo src={team.logo} alt={team.name} size="xs" className="ml-1" primaryColor={team.primaryColor} secondaryColor={team.secondaryColor} countryCode={team.countryCode} countryFlag={team.countryFlag} /><span className="flex-1 text-sm font-bold ml-2 uppercase tracking-wide">{code}</span><span className="w-9 text-sm font-bold text-right tabular-nums">{points}</span><span className="w-8 text-sm opacity-60 text-right tabular-nums">{played}</span><span className="w-8 text-sm opacity-60 text-right tabular-nums">{gd}</span></Link>;
          })}</div></>;
    };
    $[10] = labels;
    $[11] = maxRows;
    $[12] = nav;
    $[13] = stages;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const renderTable = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      backgroundColor: "var(--table-bg)"
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== competitionLogo || $[17] !== fullTableUrl || $[18] !== labels || $[19] !== showHeader) {
    t8 = showHeader && <div className="flex items-center justify-between px-4 py-3 btn-primary rounded-none"><div className="flex items-center gap-2.5">{competitionLogo && <img src={competitionLogo} alt="" className="w-7 h-7 object-contain" />}<span className="text-sm font-extrabold uppercase tracking-wide">{labels.homePage.standings}</span></div><Link prefetch={false} href={fullTableUrl} className="flex items-center justify-center w-8 h-8 rounded-full border border-current/30 hover:bg-current/10 transition-colors cursor-pointer" aria-label={labels.homePage.fullTable}><ArrowRight className="w-4 h-4" /></Link></div>;
    $[16] = competitionLogo;
    $[17] = fullTableUrl;
    $[18] = labels;
    $[19] = showHeader;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== renderTable || $[22] !== tabs) {
    t9 = <StageTabs tabs={tabs} renderBody={renderTable} />;
    $[21] = renderTable;
    $[22] = tabs;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <div className="rounded-lg overflow-hidden h-full flex flex-col border-8 border-background" style={t7}><div className="table-surface flex-1 flex flex-col overflow-hidden rounded-none">{t8}{t9}</div></div>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};